import { ProfileTypes } from '@/constants/applicationProfile'
import { DOCUMENT_OPTIONS } from '@/constants/documents'
import { UserDocument } from '@/types/unlock'

// Available eSignature Document Labels
export const ESIGN_DOC_INITIAL_APPLICATION = 'Initial Application'
export const ESIGN_DOC_INVESTMENT_ESTIMATE = 'Investment Estimate'
export const ESIGN_DOC_INVESTMENT_CLOSING_STATEMENT = 'Investment Closing Statement'
export const ESIGN_DOC_FINAL_APPLICATION = 'Final Application'
export const ESIGN_DOC_NON_APPLICANT_DISCLOSURE = 'Non-Applicant Disclosure'
export const ESIGN_DOC_ICS_NON_APPLICANT_ACKNOWLEDGEMENT = 'ICS Non-Applicant Acknowledgement'
export const ESIGN_DOC_OTHER = 'Other'

// Type Definitions
export type EsignDocumentName =
  | typeof ESIGN_DOC_INITIAL_APPLICATION
  | typeof ESIGN_DOC_INVESTMENT_ESTIMATE
  | typeof ESIGN_DOC_INVESTMENT_CLOSING_STATEMENT
  | typeof ESIGN_DOC_FINAL_APPLICATION
  | typeof ESIGN_DOC_NON_APPLICANT_DISCLOSURE
  | typeof ESIGN_DOC_ICS_NON_APPLICANT_ACKNOWLEDGEMENT
  | typeof ESIGN_DOC_OTHER

export type ProfileTypeMappingKey =
  | typeof ProfileTypes.APPLICANT
  | typeof ProfileTypes.CO_OWNER
  | typeof ProfileTypes.NOO

export interface PackageOption {
  value: string
  label: string
}

interface DocumentMappingEntry {
  defaultPackage: string | null
  availablePackages: PackageOption[]
}

type DocumentMapping = {
  [docKey in EsignDocumentName]: {
    [profileType in ProfileTypeMappingKey]: DocumentMappingEntry
  }
}

// Individual Package Option Constants
const CREDIT_CONSENT_OPTION: PackageOption = { value: 'credit-consent', label: 'Credit Consent' }
const ADDITIONAL_INITIAL_OPTION: PackageOption = {
  value: 'additional_initial',
  label: 'Initial Application',
}
const INITIAL_PACKAGE_OPTION: PackageOption = {
  value: 'initial',
  label: 'Initial Application Package',
}
const IE_OPTION: PackageOption = { value: 'ie', label: 'Investment Estimate' }
const ICS_PACKAGE_OPTION: PackageOption = { value: 'ics', label: 'ICS Package' }
const FINAL_APP_OPTION: PackageOption = { value: 'application', label: 'Final Application' }
const NON_APP_AUTH_OPTION: PackageOption = {
  value: 'non-applicants-authorization',
  label: 'Non-Applicant Disclosure Package',
}
const ICS_ACK_NON_APP_OPTION: PackageOption = {
  value: 'ics-acknowledgment-non-applicant',
  label: 'ICS Non-Applicant Acknowledgement',
}

// Package Array Constants
const INITIAL_PACKAGES: PackageOption[] = [
  CREDIT_CONSENT_OPTION,
  ADDITIONAL_INITIAL_OPTION,
  INITIAL_PACKAGE_OPTION,
]

const IE_PACKAGES: PackageOption[] = [...INITIAL_PACKAGES, IE_OPTION]

const ICS_FINAL_PACKAGES: PackageOption[] = [...IE_PACKAGES, ICS_PACKAGE_OPTION, FINAL_APP_OPTION]

const NON_APPLICANT_DISCLOSURE_PACKAGE_OPTION: PackageOption[] = [NON_APP_AUTH_OPTION]
const ICS_NON_APPLICANT_ACK_PACKAGE_OPTION: PackageOption[] = [ICS_ACK_NON_APP_OPTION]

// Document Mapping Entry Constants
const APPLICANT_INITIAL_ENTRY: DocumentMappingEntry = {
  defaultPackage: INITIAL_PACKAGE_OPTION.value,
  availablePackages: INITIAL_PACKAGES,
}

const NON_APPLICANT_INITIAL_ENTRY: DocumentMappingEntry = {
  defaultPackage: NON_APP_AUTH_OPTION.value,
  availablePackages: NON_APPLICANT_DISCLOSURE_PACKAGE_OPTION,
}

const APPLICANT_IE_ENTRY: DocumentMappingEntry = {
  defaultPackage: IE_OPTION.value,
  availablePackages: IE_PACKAGES,
}

const NON_APPLICANT_IE_ENTRY: DocumentMappingEntry = {
  defaultPackage: null,
  availablePackages: NON_APPLICANT_DISCLOSURE_PACKAGE_OPTION,
}

const APPLICANT_ICS_FINAL_ENTRY: DocumentMappingEntry = {
  defaultPackage: ICS_PACKAGE_OPTION.value,
  availablePackages: ICS_FINAL_PACKAGES,
}

const NON_APPLICANT_ICS_FINAL_ENTRY: DocumentMappingEntry = {
  defaultPackage: ICS_ACK_NON_APP_OPTION.value,
  availablePackages: [
    ...ICS_NON_APPLICANT_ACK_PACKAGE_OPTION,
    ...NON_APPLICANT_DISCLOSURE_PACKAGE_OPTION,
  ],
}

const APPLICANT_NON_APP_DISCLOSURE_ENTRY: DocumentMappingEntry = {
  defaultPackage: null,
  availablePackages: INITIAL_PACKAGES,
}

const APPLICANT_ICS_ACK_ENTRY: DocumentMappingEntry = {
  defaultPackage: null,
  availablePackages: ICS_FINAL_PACKAGES,
}

const NON_APPLICANT_ICS_ACK_ENTRY: DocumentMappingEntry = {
  defaultPackage: ICS_ACK_NON_APP_OPTION.value,
  availablePackages: [
    ...ICS_NON_APPLICANT_ACK_PACKAGE_OPTION,
    ...NON_APPLICANT_DISCLOSURE_PACKAGE_OPTION,
  ],
}

// Main E-Signature Mapping Object
const E_SIGNATURE_MAPPING: DocumentMapping = {
  [ESIGN_DOC_INITIAL_APPLICATION]: {
    [ProfileTypes.APPLICANT]: APPLICANT_INITIAL_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_INITIAL_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_INITIAL_ENTRY,
  },
  [ESIGN_DOC_INVESTMENT_ESTIMATE]: {
    [ProfileTypes.APPLICANT]: APPLICANT_IE_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_IE_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_IE_ENTRY,
  },
  [ESIGN_DOC_INVESTMENT_CLOSING_STATEMENT]: {
    [ProfileTypes.APPLICANT]: APPLICANT_ICS_FINAL_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_ICS_FINAL_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_ICS_FINAL_ENTRY,
  },
  [ESIGN_DOC_FINAL_APPLICATION]: {
    [ProfileTypes.APPLICANT]: APPLICANT_ICS_FINAL_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_ICS_FINAL_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_ICS_FINAL_ENTRY,
  },
  [ESIGN_DOC_NON_APPLICANT_DISCLOSURE]: {
    [ProfileTypes.APPLICANT]: APPLICANT_NON_APP_DISCLOSURE_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_INITIAL_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_INITIAL_ENTRY,
  },
  [ESIGN_DOC_ICS_NON_APPLICANT_ACKNOWLEDGEMENT]: {
    [ProfileTypes.APPLICANT]: APPLICANT_ICS_ACK_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_ICS_ACK_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_ICS_ACK_ENTRY,
  },
  [ESIGN_DOC_OTHER]: {
    [ProfileTypes.APPLICANT]: APPLICANT_ICS_ACK_ENTRY,
    [ProfileTypes.CO_OWNER]: NON_APPLICANT_IE_ENTRY,
    [ProfileTypes.NOO]: NON_APPLICANT_IE_ENTRY,
  },
}

export function getDocumentLabel(doc: UserDocument | null): EsignDocumentName {
  switch (doc?.document) {
    case DOCUMENT_OPTIONS.INITIAL_APPLICATION_UNLOCK_AGREEMENT:
      return ESIGN_DOC_INITIAL_APPLICATION
    case DOCUMENT_OPTIONS.INVESTMENT_ESTIMATE:
      return ESIGN_DOC_INVESTMENT_ESTIMATE
    case DOCUMENT_OPTIONS.CLOSING_STATEMENT:
      return ESIGN_DOC_INVESTMENT_CLOSING_STATEMENT
    case DOCUMENT_OPTIONS.FINAL_APPLICATION_FOR_UNLOCK_AGREEMENT:
      return ESIGN_DOC_FINAL_APPLICATION
    case DOCUMENT_OPTIONS.CLOSING_STATEMENT_NON_APPLICANT_ACKNOWLEDGEMENT:
      return ESIGN_DOC_ICS_NON_APPLICANT_ACKNOWLEDGEMENT
    default:
      if (doc?.kind === 'disclosure--non-applicant-disclosure') {
        // This kind is not being assigned any `document` attribute
        return ESIGN_DOC_NON_APPLICANT_DISCLOSURE
      }
      return ESIGN_DOC_OTHER
  }
}

export function getDefaultESignPackage(
  documentName: EsignDocumentName,
  profileType: ProfileTypeMappingKey
): string | null {
  const mapping = E_SIGNATURE_MAPPING[documentName]?.[profileType]
  if (mapping) {
    return mapping.defaultPackage
  }
  return E_SIGNATURE_MAPPING[ESIGN_DOC_OTHER]?.[profileType]?.defaultPackage ?? null
}

export function getAvailableESignPackages(
  documentName: EsignDocumentName,
  profileType: ProfileTypeMappingKey
): PackageOption[] {
  const mapping = E_SIGNATURE_MAPPING[documentName]?.[profileType]
  if (mapping) {
    return mapping.availablePackages
  }
  return E_SIGNATURE_MAPPING[ESIGN_DOC_OTHER]?.[profileType]?.availablePackages ?? []
}
